// What the user was in the middle of when we sent them to an OAuth screen — New-Front's
// `connectIntent.js` (ORP-24744), ported.
//
// Clicking a connector whose account-level identity was never authorized opens the "authenticate
// first" prompt, and that prompt leaves the SPA. Without this the user came back to Summary and had
// to find the connector again; with it they come back to Connections with its modal open.
//
// `sessionStorage` because the leg in between is a full-page navigation — a ref would be gone — and
// because the intent must not outlive the tab: a record found in a new session is somebody
// re-opening Connections, not somebody returning from a consent screen.
const STORAGE_KEY = 'orphex:integration-connect-intent'

/** Long enough for a consent screen with a sign-in and a 2FA step, short enough for lunch. */
export const CONNECT_INTENT_MAX_AGE_MS = 15 * 60 * 1000

export interface ConnectIntent {
  /** `IntegrationProvider.key` of the tile the user clicked. */
  providerKey: string
  /** `AuthFamily.connectedKey` — the flag that proves the sign-in landed. */
  connectedKey: string
  at: number
}

function storage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    // Blocked storage (private mode, hardened settings) is not an error: the return leg simply does
    // not reopen the modal, which is what happened before this existed.
    return null
  }
}

/** Remember the tile, right before leaving for OAuth. */
export function rememberConnectIntent(
  intent: Omit<ConnectIntent, 'at'>,
  now: number = Date.now(),
): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify({ ...intent, at: now }))
  } catch {
    // Quota/privacy mode — the connect still works, it just does not resume.
  }
}

export function clearConnectIntent(): void {
  try {
    storage()?.removeItem(STORAGE_KEY)
  } catch {
    // Unreadable either way.
  }
}

function rawIntent(): string | null {
  try {
    return storage()?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

function read(now: number): ConnectIntent | null {
  const raw = rawIntent()
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<ConnectIntent>
    if (typeof parsed.providerKey !== 'string' || typeof parsed.connectedKey !== 'string')
      return null
    if (typeof parsed.at !== 'number' || now - parsed.at > CONNECT_INTENT_MAX_AGE_MS) return null
    return { providerKey: parsed.providerKey, connectedKey: parsed.connectedKey, at: parsed.at }
  } catch {
    return null
  }
}

/** Whether a live intent is waiting — the callback asks this to pick its destination. */
export function hasConnectIntent(now: number = Date.now()): boolean {
  return read(now) !== null
}

/**
 * Read the intent and consume it, so a manual reload never reopens the same modal twice. A
 * malformed or stale record is dropped rather than acted on: the modal it would open is a step the
 * user did not just ask for.
 */
export function takeConnectIntent(now: number = Date.now()): ConnectIntent | null {
  const intent = read(now)
  clearConnectIntent()
  return intent
}
